import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Camera, Baby, Heart, Church } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ChristmasHero } from '../components/ChristmasHero';

// Seasonal campaign: replaces the standard hero. Set to false after Christmas.
const CHRISTMAS_CAMPAIGN = true;

// Modern, warm images - Family playing together on grass
const HERO_IMAGE = 'https://images.unsplash.com/photo-1612242879330-cd06b2696e56?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';

const portfolioImages = [
  { 
    url: '/images/family.jpg',
    category: 'family'
  },
  { 
    url: '/images/newborn.jpg',
    category: 'newborn'
  },
  { 
    url: '/images/pregnancy.jpg',
    category: 'maternity'
  },
  { 
    url: '/images/baptism.jpg',
    category: 'baptism'
  },  
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' },
  viewport: { once: true }
};

const stagger = {
  container: {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.12 }
    }
  },
  item: {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  }
};

export default function Home() {
  const { t } = useLanguage();

  const services = [
    { icon: Camera, key: 'family', image: '/images/family-mini.jpg' },
      { icon: Baby, key: 'newborn', image: '/images/newborn-mini.jpg' },
      { icon: Heart, key: 'maternity', image: '/images/pregnancy-mini.jpg' },
      { icon: Church, key: 'baptism', image: '/images/baptism-mini.jpg' },
  ];

  return (
    <div data-testid="home-page" className="bg-warm-50">
      {CHRISTMAS_CAMPAIGN ? <ChristmasHero /> : (
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Family photography"
            className="w-full h-full object-cover image-warm"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-warm-50/95 via-warm-50/70 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-xl"
          >
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-accent font-medium text-sm tracking-wider uppercase mb-4 block"
            >
              {t.hero.subtitle}
            </motion.span>

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-warm-900 mb-4 leading-tight">
              {t.hero.title}
              <br />
              <span className="text-accent">{t.hero.titleAccent}</span>
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-warm-600 text-lg leading-relaxed mb-8 max-w-md"
            >
              {t.hero.description}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/gallery" data-testid="hero-cta-portfolio">
                <Button className="bg-accent hover:bg-accent-dark text-white font-medium px-8 py-3 rounded-full transition-all duration-300 hover:shadow-lg">
                  {t.hero.cta}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/contact#booking" data-testid="hero-cta-book">
                <Button
                  variant="outline"
                  className="border-2 border-accent text-accent hover:bg-accent hover:text-white px-8 py-3 rounded-full transition-all duration-300"
                >
                  {t.hero.ctaSecondary}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-warm-400 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-1.5 bg-accent rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>
      )}

      {/* Services Preview */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-14">
            <span className="text-accent font-medium text-sm tracking-wider uppercase mb-3 block">
              {t.services.subtitle}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-warm-900">
              {t.services.title}
            </h2>
          </motion.div>

          <motion.div
            variants={stagger.container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {services.map((service) => {
              const Icon = service.icon;
              const serviceData = t.services[service.key];
              return (
                <motion.div
                  key={service.key}
                  variants={stagger.item}
                  data-testid={`service-card-${service.key}`}
                  className="group bg-warm-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={service.image}
                      alt={serviceData.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-accent" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-warm-900 mb-2">
                      {serviceData.title}
                    </h3>
                    <p className="text-warm-600 text-base leading-relaxed">
                      {serviceData.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          <motion.div {...fadeUp} className="text-center mt-10">
            <Link to="/services" data-testid="services-link">
              <Button
                variant="ghost"
                className="text-accent hover:text-accent-dark font-medium"
              >
                {t.services.title}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="py-20 md:py-28 bg-warm-100">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-14">
            <span className="text-accent font-medium text-sm tracking-wider uppercase mb-3 block">
              {t.gallery.subtitle}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-warm-900">
              {t.gallery.title}
            </h2>
          </motion.div>

          <motion.div
            variants={stagger.container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            {portfolioImages.map((img, index) => (
              <motion.div
                key={index}
                variants={stagger.item}
                className={`relative overflow-hidden rounded-2xl group ${
                  index === 0 ? 'md:row-span-2' : ''
                }`}
              >
                <div className={`aspect-[4/5] ${index === 0 ? 'md:h-full' : ''}`}>
                  <img
                    src={img.url}
                    alt={`Portfolio ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-warm-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div {...fadeUp} className="text-center mt-10">
            <Link to="/gallery" data-testid="gallery-link">
              <Button className="bg-accent hover:bg-accent-dark text-white font-medium px-8 py-3 rounded-full transition-all duration-300 hover:shadow-lg">
                {t.hero.cta}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-warm-200/50 rounded-full blur-3xl" />

        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-warm-900 mb-6">
              {t.contact.subtitle}
            </h2>
            <p className="text-warm-600 text-lg leading-relaxed mb-8 max-w-xl mx-auto">
              {t.contact.intro}
            </p>
            <Link to="/contact" data-testid="cta-contact">
              <Button className="bg-accent hover:bg-accent-dark text-white font-medium px-10 py-4 rounded-full transition-all duration-300 hover:shadow-lg text-base">
                {t.nav.contact}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
